import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
  DataError,
  IconButton,
  SearchInput,
  Table,
  TableDataCell,
  TableHeadCell,
  Tooltip
} from "@digital-services/ui";
import { Pagination, useSortQueryString } from "@ds-cps/ui";

import { AppEnv } from "@application/components/AppEnv";
import { ApplicationJsonApiResponse } from "@application/model/ApplicationJsonModel";
import {
  filter,
  sort,
  useGetOrganizationApplications
} from "@organization/services/use-get-organization-applications";

import { useUserContext } from "@hooks/useUserContext";
import TrashIcon from "@icons/trash.svg?react";

import { AssociateApplicationButton } from "./AssociateApplicationButton";
import { RemoveApplicationAssociationButton } from "./RemoveApplicationAssociationButton";

export const OrganizationApplications: FunctionComponent = () => {
  const { organizationId } = useParams();
  const { user } = useUserContext();

  const { t: tApplications } = useTranslation(undefined, {
    keyPrefix: "organization.applications"
  });
  const { t: tApplication } = useTranslation(undefined, {
    keyPrefix: "organization.application"
  });

  const [searchValue, setSearchValue] = useState<string | undefined>("");

  // Tech Note. In this component Sorting of table entries done via JavaScript aka Frontend Sorting
  const { activeSort, sortDirection, handleSort } = useSortQueryString();

  const { associatedApplications, isLoading, error, mutate } =
    useGetOrganizationApplications(organizationId ?? "");

  const [filteredApplications, setFilteredApplications] = useState<
    ApplicationJsonApiResponse[] | null
  >(null);
  const [organizationApplications, setOrganizationApplications] = useState<
    ApplicationJsonApiResponse[] | null
  >(null);

  const nameColumnName = "name";
  const appEnvColumnName = "appEnv";
  const actionsColumnName = "actions";

  useEffect(() => {
    const applications = filter(associatedApplications, searchValue);
    setFilteredApplications(applications);
  }, [organizationId, associatedApplications, searchValue]);

  useEffect(() => {
    const applications = sort(filteredApplications, activeSort, sortDirection);
    setOrganizationApplications(applications);
  }, [filteredApplications, activeSort, sortDirection]);

  function renderNameHeader() {
    return (
      <TableHeadCell
        sortActive={activeSort === nameColumnName}
        sortDirection={sortDirection}
        onSort={() => handleSort(nameColumnName)}
        data-testid={`${nameColumnName}-column`}
      >
        <span>{tApplication(`${nameColumnName}.column-header`)}</span>
      </TableHeadCell>
    );
  }

  function renderAppEnvHeader() {
    return (
      <TableHeadCell
        sortActive={activeSort === appEnvColumnName}
        sortDirection={sortDirection}
        onSort={() => handleSort(appEnvColumnName)}
        data-testid={`${appEnvColumnName}-column`}
      >
        <span>{tApplication(`${appEnvColumnName}.column-header`)}</span>
      </TableHeadCell>
    );
  }

  const renderActionTableHeadCell = () => {
    return (
      <TableHeadCell>
        <div data-testid={`${actionsColumnName}-column`}>
          {tApplication(`${actionsColumnName}.column-header`)}
        </div>
      </TableHeadCell>
    );
  };

  // Relying on useCallback() is workaround to address issue with `<SearchInput>` whose `onChange()` is executed twice.
  const handleSearchInput = useCallback((newValue: string) => {
    setSearchValue(newValue);
  }, []);

  return (
    <div
      data-testid="organization-application-table-container"
      className="mt-3 bg-white p-4"
    >
      <div className="mb-3 flex items-center gap-3">
        <div className="grow"></div>
        <div className="w-[500px]">
          <SearchInput
            data-testid="search-association"
            placeholder={tApplications("search.placeholder")}
            onChange={handleSearchInput}
          />
        </div>
        {user.isSuperAdmin ? (
          <AssociateApplicationButton
            organizationId={organizationId ?? ""}
            afterCallback={() => {
              mutate();
            }}
          />
        ) : null}
      </div>

      {error ? (
        <DataError
          title={tApplications("load.error.message")}
          message=""
          className="text-left! text-red-600"
        />
      ) : (
        <>
          <Table className="w-full" isLoading={isLoading} rowHoverHighlight>
            <thead data-testid="thead">
              <tr className="leading-[0px]">
                {renderNameHeader()}
                {renderAppEnvHeader()}
                {renderActionTableHeadCell()}
              </tr>
            </thead>
            <tbody data-testid="tbody">
              {organizationApplications?.map(
                (appResponse: ApplicationJsonApiResponse) => {
                  const application = appResponse.data;
                  const appError = appResponse.error;

                  const runtimeAppName = appError ? (
                    <Tooltip message={appError}>
                      <span className="text-red-500">{application.name}</span>
                    </Tooltip>
                  ) : (
                    application.name
                  );

                  return (
                    <tr
                      key={`applications-table-row-${application.id}`}
                      data-testid={`applications-table-row-${application.id}`}
                    >
                      <TableDataCell>{runtimeAppName}</TableDataCell>
                      <TableDataCell>
                        {application.appEnv ? (
                          <AppEnv value={application.appEnv} />
                        ) : null}
                      </TableDataCell>
                      <TableDataCell align="left" className="py-0!">
                        {!user.isSuperAdmin ? null : appError ? (
                          <Tooltip message={appError}>
                            <IconButton
                              className="text-red-500!"
                              data-testid={`delete-icon-button-${application.id}`}
                              disabled={true}
                            >
                              <TrashIcon className="h-3 fill-red-500! sm:h-4" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <RemoveApplicationAssociationButton
                            organizationId={organizationId ?? ""}
                            application={application}
                            afterCallback={() => {
                              mutate();
                            }}
                          />
                        )}
                      </TableDataCell>
                    </tr>
                  );
                }
              )}
            </tbody>
          </Table>

          {organizationApplications && organizationApplications.length > 0 ? (
            <Pagination totalCount={organizationApplications?.length} />
          ) : null}
        </>
      )}
    </div>
  );
};
